/**
 * War plans (Phase 13c, ADR-0071): what an AI civilisation means to take in a war, and
 * how its army goes about it.
 *
 * Before this, every soldier at war walked to the nearest enemy city on its own and
 * arrived alone; the war ended on a timer whether anything had been won or not. Now a
 * war has an aim — `warAims` enemy cities, nearest first — and the army gathers at the
 * home city nearest the first of them until it is strong enough, marches together, and
 * pulls back to gather again when it has lost too many on the way. The plan is kept on
 * the relation (`campaignA` / `campaignB`), so a reload carries on the same war.
 *
 * Overseas wars keep the ferrying of Phase 7b (`directShip`): there is no road to march.
 */

import type { Content } from '../../../content/schema/index.js';
import { cityIndex, citiesOf } from '../cities/index.js';
import { isCapital } from '../cities/economy.js';
import { techBlocker } from '../cities/production.js';
import { wallsOf } from '../combat/index.js';
import { liftFor, stagingPort } from './sealift.js';
import { canAttack, canDefend, isSiege } from '../combat/index.js';
import { enemiesOf, relationOf } from '../diplomacy/index.js';
import { findPath } from '../path/astar.js';
import { wrappedDistance } from '../state/start.js';
import { NONE, VIS_VISIBLE, type Campaign, type Civ, type GameState, type Id, type Unit } from '../state/types.js';
import { allUnits, unitDef, unitIndex, unitsOf } from '../units/index.js';
import { weightsFor } from './personality.js';

/** This civilisation's plan for its war with `enemy`, or null. */
export function campaignOf(state: GameState, civId: Id, enemy: Id): Campaign | null {
  const relation = relationOf(state, civId, enemy);
  if (!relation || !relation.war) return null;
  return relation.a === civId ? relation.campaignA : relation.campaignB;
}

function setCampaign(state: GameState, civId: Id, enemy: Id, campaign: Campaign | null): void {
  const relation = relationOf(state, civId, enemy);
  if (!relation) return;
  if (relation.a === civId) relation.campaignA = campaign;
  else relation.campaignB = campaign;
}

/** The home city nearest a tile, by straight distance. */
function nearestOwnCity(state: GameState, civId: Id, to: number): number | null {
  let best: number | null = null;
  let bestDistance = Infinity;
  for (const city of citiesOf(state, civId)) {
    const at = cityIndex(state.map, city);
    const distance = wrappedDistance(state.map, at, to);
    if (distance < bestDistance || (distance === bestDistance && best !== null && at < best)) {
      bestDistance = distance;
      best = at;
    }
  }
  return best;
}

/**
 * The enemy cities a war should be for: ones this civilisation has seen, nearest its own
 * cities first, a capital counted `aimCapitalPenalty` tiles further away (it is the
 * best defended). At most `count`.
 */
export function chooseAims(state: GameState, content: Content, civId: Id, enemy: Id, count: number, skip: readonly Id[] = []): Id[] {
  const ai = weightsFor(content, state.civs[civId]);
  const memory = state.memory[civId];
  if (!memory || count <= 0) return [];
  const scored: { id: Id; score: number }[] = [];
  for (const city of citiesOf(state, enemy)) {
    if (skip.includes(city.id)) continue;
    const at = cityIndex(state.map, city);
    if (!memory.cities[at]) continue;
    const from = nearestOwnCity(state, civId, at);
    if (from === null) continue;
    const capital = isCapital(content, city);
    scored.push({ id: city.id, score: wrappedDistance(state.map, from, at) + (capital ? ai.aimCapitalPenalty : 0) });
  }
  scored.sort((p, q) => p.score - q.score || p.id - q.id);
  return scored.slice(0, count).map((s) => s.id);
}

/**
 * The plan for this war, made the first time it is asked for and brought up to date
 * every time after: aims taken by this civilisation count towards the goal, aims lost
 * to anyone else are dropped, and when none are left short of the goal new ones are
 * chosen. Null when there is nothing known to aim at.
 */
export function ensureCampaign(state: GameState, content: Content, civId: Id, enemy: Id): Campaign | null {
  const relation = relationOf(state, civId, enemy);
  if (!relation || !relation.war) return null;
  const ai = weightsFor(content, state.civs[civId]);
  const turn = state.meta.turn;
  let campaign = campaignOf(state, civId, enemy);
  if (!campaign) {
    const aims = chooseAims(state, content, civId, enemy, ai.warAims);
    if (aims.length === 0) return null;
    // The war is for warAims cities, even if only some are known yet; more are aimed at
    // as they are found. Never more than the enemy has.
    const goal = Math.min(ai.warAims, citiesOf(state, enemy).length);
    campaign = { aims, goal, taken: 0, marching: false, marchedWith: 0, members: [], since: turn, lastGain: relation.since };
    setCampaign(state, civId, enemy, campaign);
    return campaign;
  }
  campaign.aims = campaign.aims.filter((id) => {
    const city = state.cities[id];
    if (!city) return false;
    if (city.owner === civId) {
      campaign!.taken++;
      campaign!.lastGain = turn;
      return false;
    }
    return city.owner === enemy;
  });
  if (campaign.aims.length < campaign.goal - campaign.taken) {
    const more = chooseAims(state, content, civId, enemy, campaign.goal - campaign.taken - campaign.aims.length, campaign.aims);
    campaign.aims.push(...more);
  }
  return campaign;
}

/** Whether a war's aim has been achieved: it has taken every city it went to war for. */
export function aimMet(campaign: Campaign | null): boolean {
  return campaign !== null && campaign.taken >= campaign.goal;
}

// ─── the army ──────────────────────────────────────────────────────────────────

/** Where this turn's war is being fought, as the soldiers need to know it. */
export interface Front {
  enemy: Id;
  /** The enemy city being marched on. */
  target: number;
  /** The home city the army gathers in. */
  staging: number;
  marching: boolean;
  /** Units of the field army: those who set out. The rest of the army gathers. */
  field: Set<Id>;
  /** The furthest from the target any of the field army is, so nobody runs ahead. */
  rear: number;
  /** Soldiers and siege engines the plan still lacks, for the cities to build (ADR-0072). */
  needSoldiers: number;
  needSiege: number;
  /** Over the sea: `staging` is the port the invasion sails from (ADR-0072). */
  overseas: boolean;
  /** Room aboard the plan still lacks, in soldiers, for the cities to build transports. */
  needLift: number;
}

/** Whether this civilisation knows how to build any siege engine. */
function canBuildSiege(content: Content, civ: Civ): boolean {
  return content.units.all.some((u) => isSiege(u) && techBlocker(content, civ, { kind: 'unit', type: u.id }) === null);
}

/**
 * Whether a unit type belongs in a field army: a land unit at least as good in attack
 * as in defence, or a siege engine. Spearmen and their like hold walls; sent to take
 * one, they only die under it (ADR-0071).
 */
export function isArmyType(def: Parameters<typeof canAttack>[0]): boolean {
  return def.domain === 'land' && canAttack(def) && (def.attack >= def.defence || isSiege(def));
}

/** Soldiers free to fight away from home: army types, not holding a city, not at sea. */
export function armyOf(state: GameState, content: Content, civId: Id, posts: ReadonlyMap<Id, Id>): Unit[] {
  return unitsOf(state, civId).filter((u) => !posts.has(u.id) && u.aboard === NONE && isArmyType(unitDef(content, u)));
}

/** How many defenders the army expects to meet: the ones it can see, or a guess. */
function defendersAt(state: GameState, content: Content, civId: Id, target: number): number {
  const vision = state.vision[civId];
  if (!vision || vision[target] !== VIS_VISIBLE) return weightsFor(content, state.civs[civId]).armyGuessDefenders;
  let n = 0;
  for (const unit of allUnits(state)) {
    if (unit.owner === civId || unitIndex(state.map, unit) !== target) continue;
    if (canDefend(unitDef(content, unit))) n++;
  }
  return n;
}

/** Whether soldiers could walk from one tile to another, as far as this civilisation knows the land. */
export function walkable(state: GameState, content: Content, civId: Id, from: number, to: number): boolean {
  const probe = content.units.all.find((u) => u.domain === 'land' && u.attack > 0);
  if (!probe) return false;
  return findPath({ content, map: state.map, def: probe, vision: state.vision[civId] ?? null }, from, to) !== null;
}

/**
 * The war this civilisation is prosecuting, if any it can reach on foot: its oldest
 * war with a plan, its first aim, where its army gathers, and whether it is marching.
 * Moves the plan between gathering and marching as the army fills or thins:
 *
 * - gathering → marching when `armyPerDefender` soldiers for each defender expected
 *   (at least `armyMinimum`) stand within `gatherRadius` of the staging city — or,
 *   after `gatherMaxTurns`, when at least `armyMinimum` do;
 * - marching → gathering when fewer than `regroupShare` of those who set out are left.
 */
export function directCampaign(state: GameState, content: Content, civId: Id, posts: ReadonlyMap<Id, Id>): Front | null {
  const ai = weightsFor(content, state.civs[civId]);
  const enemies = enemiesOf(state, civId)
    .map((id) => ({ id, since: relationOf(state, civId, id)!.since }))
    .sort((p, q) => p.since - q.since || p.id - q.id);
  for (const { id: enemy } of enemies) {
    const campaign = ensureCampaign(state, content, civId, enemy);
    if (!campaign || campaign.aims.length === 0) continue;
    const city = state.cities[campaign.aims[0]!];
    if (!city) continue;
    const target = cityIndex(state.map, city);
    const home = nearestOwnCity(state, civId, target);
    if (home === null) continue;
    // No road there: the invasion gathers at the nearest port and goes by sea (ADR-0072).
    const overseas = !walkable(state, content, civId, home, target);
    const staging = overseas ? stagingPort(state, content, civId, target) : home;
    if (staging === null) continue;
    const lift = overseas ? liftFor(state, content, civId, target) : Infinity;

    const army = armyOf(state, content, civId, posts);
    const turn = state.meta.turn;
    const needed = Math.max(ai.armyMinimum, Math.ceil(defendersAt(state, content, civId, target) * ai.armyPerDefender));
    // Siege engines lead (ADR-0072): a share of the army, and never a march on walls
    // without one — when this civilisation can build them at all.
    const walled = wallsOf(content, city) > 0;
    const siegeWanted = canBuildSiege(content, state.civs[civId]!) ? Math.max(walled ? 1 : 0, Math.ceil(needed * ai.siegeArmyShare)) : 0;
    const siegeHave = army.filter((u) => isSiege(unitDef(content, u))).length;
    const needSiege = Math.max(0, siegeWanted - siegeHave);
    const needSoldiers = Math.max(0, needed - siegeWanted - (army.length - siegeHave));
    if (!campaign.marching) {
      const near = army.filter((u) => wrappedDistance(state.map, unitIndex(state.map, u), staging) <= ai.gatherRadius);
      const nearSiege = near.filter((u) => isSiege(unitDef(content, u))).length;
      // After gatherMaxTurns it stops waiting for the full count, but not for most of
      // the army it has (gatherShare): setting out with the first few to arrive was
      // how marches died under the walls.
      const waited = turn - campaign.since >= ai.gatherMaxTurns;
      const most = Math.max(ai.armyMinimum, Math.ceil(army.length * ai.gatherShare));
      const breaker = !walled || siegeWanted === 0 || nearSiege > 0;
      // Over the sea it sets out only with room aboard for the army it means to land.
      const afloat = !overseas || lift >= Math.min(near.length, needed);
      if (afloat && ((near.length >= needed && nearSiege >= siegeWanted) || (waited && near.length >= most && breaker))) {
        campaign.marching = true;
        campaign.marchedWith = near.length;
        campaign.members = near.map((u) => u.id).sort((p, q) => p - q);
        campaign.since = turn;
      }
    }
    // On the march, the field army is those who set out and are still free to fight.
    const field = new Set<Id>();
    let rear = 0;
    if (campaign.marching) {
      // Members at sea are still members.
      const free = new Set(army.map((u) => u.id));
      campaign.members = campaign.members.filter((id) => free.has(id) || (state.units[id]?.owner === civId && state.units[id]!.aboard !== NONE));
      // Soldiers who reach the staging city after the army left follow it.
      for (const unit of army) {
        if (campaign.members.includes(unit.id)) continue;
        if (wrappedDistance(state.map, unitIndex(state.map, unit), staging) <= ai.gatherRadius) campaign.members.push(unit.id);
      }
      campaign.members.sort((p, q) => p - q);
      const distances = campaign.members.map((id) => wrappedDistance(state.map, unitIndex(state.map, state.units[id]!), target));
      for (const id of campaign.members) field.add(id);
      // The column keeps pace with its slower soldiers — not the slowest quarter, who
      // may be stuck — and once its head is at the walls nobody waits for anybody.
      // A landing goes in together anyway: no waiting for anyone still at sea.
      if (!overseas && !distances.some((d) => d <= 1)) {
        const sorted = [...distances].sort((p, q) => q - p);
        rear = sorted[Math.floor(sorted.length / 4)] ?? 0;
      }
      if (field.size < campaign.marchedWith * ai.regroupShare) {
        campaign.marching = false;
        campaign.members = [];
        campaign.since = turn;
        field.clear();
        rear = 0;
      }
    }
    const needLift = overseas ? Math.max(0, Math.min(needed, army.length) - lift) : 0;
    return { enemy, target, staging, marching: campaign.marching, field, rear, needSoldiers, needSiege, overseas, needLift };
  }
  return null;
}
